import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Route, Redirect } from "react-router-dom";
import { getConnectedUserInfo } from "state/common/actions";
import Unauthorized from "./Unauthorized";
import Layout from "app/universal/layout/Layout";
import DriverHeader from "app/universal/layout/DriverHeader";
import FakeDelivery from "app/deliveryTool/components/delivery/FakeDelivery";

const PrivateRoute = ({ component: Component, roles, ...rest }) => {
  const dispatch = useDispatch();

  const [accessToken, setAccessToken] = useState(
    () => window.localStorage.getItem("token") || ""
  );
  
  const connectedUser = useSelector((state) => state.common.user.data);
  const userRoles = connectedUser?.roles || [];
  const isLoading = useSelector((state) => state.common.user.isLoading);
  const isInitialized = useSelector((state) => state.common.user.isInitialized);

  useEffect(() => {
    if (accessToken && !connectedUser?.id && !isLoading && !isInitialized) {
      dispatch(getConnectedUserInfo());
    }
  }, [accessToken, isLoading, connectedUser, isInitialized, dispatch]);

  const canAccess = () => {
    const hasCompanionAccess =
      roles.includes("companion") &&
      (userRoles.includes("ROLE_PRO_COMPANION_USER") ||
      userRoles.includes("ROLE_ADMIN_PRO"));
    const hasAdminAccess =
      roles.includes("admin") &&
      (userRoles.includes("ROLE_ADMIN") ||
        userRoles.includes("ROLE_SUPER_ADMIN") ||
        userRoles.includes("ROLE_DEV") ||
        userRoles.includes("ROLE_ADMIN_PRO")
      );
    const hasB2CLivreurAccess =
      roles.includes("b2c_livreur") &&
      userRoles.includes("ROLE_B2C_LIVREUR");
    // Infos financières sensibles : réservé ROLE_FINANCE (les devs passent aussi)
    const hasFinanceAccess =
      roles.includes("finance") && userRoles.includes("ROLE_FINANCE");
    // Dashboard data (Metabase) : réservé ROLE_STATS
    const hasStatsAccess =
      roles.includes("stats") && userRoles.includes("ROLE_STATS");
    // Pipeline et SAV B2B : réservé ROLE_ADMIN_PRO (les devs passent aussi)
    const hasProAccess =
      roles.includes("pro") && userRoles.includes("ROLE_ADMIN_PRO");
    const hasDevAccess = userRoles.includes("ROLE_DEV");

    // Une route « finance » ne doit PAS être ouverte par le simple accès admin
    if (roles.includes("finance")) {
      return { access: hasFinanceAccess || hasDevAccess, path: null };
    }

    // Une route « stats » ne doit PAS être ouverte par le simple accès admin
    if (roles.includes("stats")) {
      return { access: hasStatsAccess || hasDevAccess, path: null };
    }

    // Une route « pro » ne doit PAS être ouverte par le simple accès admin
    if (roles.includes("pro")) {
      return { access: hasProAccess || hasDevAccess, path: null };
    }

    if (hasCompanionAccess || hasAdminAccess || hasB2CLivreurAccess || hasDevAccess) {
      return { access: true, path: null };
    } else {
      return { access: false, path: null };
    }
  };

  const accessInfo = canAccess();

  // Les onglets du menu sont ceux de l'admin : un compte livreur ne doit pas le
  // voir, même sur « Accès refusé ». Livreur = fiche livreur (delivery_scope,
  // y compris hors période d'accès, sans aucun rôle) ou uniquement des rôles
  // livreur (compte charrette). Tout autre compte garde le menu, comme avant.
  const DRIVER_ROLES = ["ROLE_PRO_COMPANION_USER", "ROLE_B2C_LIVREUR"];
  const isDriver =
    connectedUser?.delivery_scope !== undefined ||
    (userRoles.length > 0 && userRoles.every((role) => DRIVER_ROLES.includes(role)));
  const hasMenu = !isDriver;

  // Afficher le loader pendant le chargement des informations utilisateur
  if (accessToken && (isLoading || !isInitialized)) {
    return (
      <Route
        {...rest}
        render={() => (
          <div className="p-4">
            <FakeDelivery />
          </div>
        )}
      />
    );
  }

  return (
    <Route
      {...rest}
      render={(props) =>
        accessToken ? (
          accessInfo.access ? (
            <Component {...props} />
          ) : hasMenu ? (
            // Admin connecté mais pas autorisé sur cette page : on garde le menu
            // pour pouvoir naviguer vers les onglets autorisés.
            <Layout>
              <Unauthorized withMenu />
            </Layout>
          ) : (
            // Livreur : pas de menu, seulement son nom et la déconnexion
            <>
              <DriverHeader logoutPath="/itineraires-b2c" />
              <Unauthorized />
            </>
          )
        ) : (
          <Unauthorized />
        )
      }
    />
  );
};

export default PrivateRoute;